import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import { ProductConfiguration } from "@domain/project/configurations/product-configuration";
import { ProjectEventType } from "@domain/project/project-event";
import type { ScopedProject } from "@domain/project/project-guards";
import { MonitoringProductItem } from "@project/monitoring-product-item";
import { Expose, Type } from "class-transformer";
import { IsEnum, IsOptional, IsString, ValidateNested } from "class-validator";
import { v4 as uuidv4 } from "uuid";

export class KvmConfiguration extends ProductConfiguration {
  @IsString()
  public familyNumber: string;

  @IsEnum(MonitoringCategory)
  @Expose({ name: "category" })
  private _category!: MonitoringCategory;

  @Type(() => MonitoringProductItem)
  @Expose({ name: "family" })
  @ValidateNested()
  private _family!: MonitoringProductItem;

  @Type(() => MonitoringProductItem)
  @Expose({ name: "transmitter" })
  @ValidateNested()
  private _transmitter!: MonitoringProductItem;

  @IsOptional()
  @Type(() => MonitoringProductItem)
  @Expose({ name: "receiver" })
  @ValidateNested()
  private _receiver?: MonitoringProductItem;

  @IsOptional()
  @Type(() => MonitoringProductItem)
  @ValidateNested({ each: true })
  @Expose({ name: "accessories" })
  private _accessories?: MonitoringProductItem[];

  constructor(
    id: string,
    name: string,
    project: ScopedProject,
    familyNumber: string,
    category: MonitoringCategory,
    family: MonitoringProductItem,
    transmitter: MonitoringProductItem,
    receiver?: MonitoringProductItem,
    accessories?: MonitoringProductItem[],
  ) {
    super(id, name, project);
    this.familyNumber = familyNumber;
    this.category = category;
    this.family = family;
    this.transmitter = transmitter;
    this.receiver = receiver;
    this.accessories = accessories;
  }

  static create(
    name: string,
    project: ScopedProject,
    familyNumber: string,
    category: MonitoringCategory,
    family: MonitoringProductItem,
    transmitter: MonitoringProductItem,
    receiver?: MonitoringProductItem,
    accessories?: MonitoringProductItem[],
  ): KvmConfiguration {
    return new KvmConfiguration(uuidv4(), name, project, familyNumber, category, family, transmitter, receiver, accessories);
  }

  update(
    familyName: string,
    familyNumber: string,
    family: MonitoringProductItem,
    transmitter: MonitoringProductItem,
    receiver?: MonitoringProductItem,
    accessories?: MonitoringProductItem[],
  ) {
    this.name = familyName;
    this.familyNumber = familyNumber;
    this.family = family;
    this.transmitter = transmitter;
    this.receiver = receiver;
    this.accessories = accessories;
    this.project.publishUpdate(ProjectEventType.KVM_CONFIGURATION_UPDATED, this);
  }

  override get mainProductId(): string {
    return this.familyNumber;
  }

  get category(): MonitoringCategory {
    return this._category;
  }

  get family(): MonitoringProductItem {
    return this._family;
  }

  get transmitter(): MonitoringProductItem {
    return this._transmitter;
  }

  get transmitterProductId(): string | undefined {
    return this._transmitter?.option?.devices?.[0];
  }

  get transmitterGroupImageCantoId(): string | undefined {
    const cantoId = this._transmitter?.option?.groupImageCantoID?.trim();
    return cantoId ? cantoId : undefined;
  }

  get receiver(): MonitoringProductItem | undefined {
    return this._receiver;
  }

  get accessories(): MonitoringProductItem[] {
    return this._accessories ?? [];
  }

  set family(value: MonitoringProductItem) {
    this._family = value;
  }

  set category(value: MonitoringCategory) {
    this._category = value;
  }

  set transmitter(value: MonitoringProductItem) {
    this._transmitter = value;
  }

  set receiver(value: MonitoringProductItem | undefined) {
    this._receiver = value;
  }

  set accessories(value: MonitoringProductItem[] | undefined) {
    this._accessories = value;
  }

  get configuredProductIds(): string[] {
    return [...this.transmitter.option.devices, ...(this.receiver?.option?.devices ?? []), ...this.accessoriesDevices];
  }

  getOptions(): MonitoringProductItem[] {
    return [this.transmitter, ...(this.receiver?.option ? [this.receiver] : []), ...(this.accessories ?? [])];
  }

  getAllItems(): MonitoringProductItem[] {
    return this.getOptions();
  }

  get accessoriesDevices(): string[] {
    return (this.accessories ?? []).flatMap((attachment) => attachment.option.devices);
  }

  countPlacedProducts(project: ScopedProject = this.project): number {
    return project ? project.countPlacedKvms(this) : 0;
  }
}
